// src/admin/components/HomePageConfig/AboutUsSection.jsx
import React from "react";
import ColorSection from "./ColorSection";
import ColorPicker from "./ColorPicker";
import FontSizeSection from "./FontSizeSection";

export default function AboutUsSection({ 
  aboutUs, 
  setAboutUs, 
  aboutUsColors = {},
  setAboutUsColors,
  uploadImageFile, 
  deleteImageFile, 
  IMAGES_BASE 
}) {
  
  // Provide default values if aboutUs is undefined
  const safeAboutUs = aboutUs || {
    display: true,
    title: "",
    subtitle: "",
    description: "",
    image: "",
    yearsExperience: "",
    projectsCompleted: "",
    buttonText: "",
    buttonLink: "",
    backgroundColor: "#ffffff",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  };
  
  // Provide default values if aboutUsColors is undefined
  const safeAboutUsColors = aboutUsColors || {
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    statsColor: "#000000",
    buttonTextColor: "#ffffff",
    buttonBgColor: "#000000",
  };
  
  const handleAboutUsImageUpload = async (file) => {
    if (!file) return;
    const filename = await uploadImageFile(file);
    if (filename) {
      setAboutUs((prev) => ({ ...(prev || {}), image: filename }));
    }
  };

  const handleAboutUsImageDelete = async () => {
    if (!safeAboutUs.image) return;
    const ok = await deleteImageFile(safeAboutUs.image);
    if (ok) {
      setAboutUs((prev) => ({ ...(prev || {}), image: "" }));
    }
  };

  const handleColorChange = (property, value) => {
    if (setAboutUsColors) {
      setAboutUsColors(prev => ({
        ...(prev || {}),
        [property]: value === "transparent" ? "transparent" : value
      }));
    }
  };

  const handleAboutUsChange = (property, value) => {
    setAboutUs((prev) => ({
      ...(prev || {}),
      [property]: value
    }));
  };

  const toggleSectionColors = (useSectionColors) => {
    setAboutUs(prev => ({
      ...(prev || {}),
      useSectionColors
    }));
  };

  const toggleSectionFontSizes = (useSectionFontSizes) => {
    setAboutUs((prev) => ({
      ...(prev || {}),
      useSectionFontSizes,
    }));
  };

  const handleFontSizeChange = (key, value) => {
    setAboutUs((prev) => ({
      ...(prev || {}),
      fontSizes: {
        ...(prev?.fontSizes || {}),
        [key]: value,
      },
    }));
  };

  return (
    <div className="hcfg-card">
      <div className="hcfg-headrow">
        <h3>About Us Section</h3>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={safeAboutUs.display}
            onChange={(e) =>
              setAboutUs((prev) => ({ ...(prev || {}), display: e.target.checked }))
            }
            style={{ width: "18px", height: "18px", cursor: "pointer" }}
          />
          Show on Home Page
        </label>
      </div>

      <div
        className="hcfg-row"
        style={{
          opacity: safeAboutUs.display ? 1 : 0.6,
          pointerEvents: safeAboutUs.display ? "auto" : "none",
        }}
      >
        <div className="hcfg-col img">
          <h4 style={{ marginBottom: "10px" }}>About Us Image</h4>
          {safeAboutUs.image ? (
            <img
              src={`${IMAGES_BASE}/${encodeURIComponent(safeAboutUs.image)}`}
              alt="About Us preview"
              className="hcfg-preview"
              style={{ 
                width: "100%", 
                maxHeight: "200px", 
                objectFit: "cover",
                borderRadius: "4px"
              }}
            />
          ) : (
            <div className="hcfg-placeholder" style={{
              height: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
              borderRadius: "4px",
              color: "#666"
            }}>
              No image selected
            </div>
          )}
          <div className="hcfg-actions-inline" style={{ marginTop: "10px" }}>
            <label className="hcfg-btn" style={{
              padding: "8px 16px",
              background: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "inline-block"
            }}>
              Upload Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleAboutUsImageUpload(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </label>
            <button 
              className="hcfg-btn danger" 
              onClick={handleAboutUsImageDelete}
              style={{
                padding: "8px 16px",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px"
              }}
            >
              Delete Image
            </button>
          </div>
        </div>

        <div className="hcfg-col">
          <label style={{ display: "block", marginBottom: "5px" }}>Head (Title)</label>
          <input
            value={safeAboutUs.title || ""}
            onChange={(e) => handleAboutUsChange("title", e.target.value)}
            placeholder="e.g. Who We Are"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>

        <div className="hcfg-col">
          <label style={{ display: "block", marginBottom: "5px" }}>Sub Head</label>
          <input
            value={safeAboutUs.subtitle || ""}
            onChange={(e) => handleAboutUsChange("subtitle", e.target.value)}
            placeholder="e.g. 20 Years of Excellence"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
      </div>

      <div className="hcfg-row">
        <div className="hcfg-col" style={{ gridColumn: "span 2" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>Content (Description)</label>
          <textarea
            rows={5}
            value={safeAboutUs.description || ""}
            onChange={(e) => handleAboutUsChange("description", e.target.value)}
            placeholder="Tell your story..."
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
        </div>

        <div className="hcfg-col">
          <label style={{ display: "block", marginBottom: "5px" }}>Years of Experience</label>
          <input
            value={safeAboutUs.yearsExperience || ""}
            onChange={(e) => handleAboutUsChange("yearsExperience", e.target.value)}
            placeholder="e.g. 15+"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />
          
          <label style={{ display: "block", marginBottom: "5px" }}>Projects Completed</label>
          <input
            value={safeAboutUs.projectsCompleted || ""}
            onChange={(e) => handleAboutUsChange("projectsCompleted", e.target.value)}
            placeholder="e.g. 350+"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />

          <div style={{ height: "1px", background: "#eee", margin: "15px 0" }} />

          <label style={{ display: "block", marginBottom: "5px" }}>Button Text</label>
          <input
            value={safeAboutUs.buttonText || ""}
            onChange={(e) => handleAboutUsChange("buttonText", e.target.value)}
            placeholder="e.g. Read More"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />
          
          <label style={{ display: "block", marginBottom: "5px" }}>Button Link</label>
          <input
            value={safeAboutUs.buttonLink || ""}
            onChange={(e) => handleAboutUsChange("buttonLink", e.target.value)}
            placeholder="/about-us"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
      </div>

      {/* About Us Section Background Color */}
      <div className="hcfg-row" style={{ marginTop: "20px" }}>
        <div className="hcfg-col">
          <h4 style={{ marginBottom: "15px" }}>About Us Section Background</h4>
          <ColorPicker
            label="Section Background Color"
            value={safeAboutUs.backgroundColor || "#ffffff"}
            onChange={(value) => 
              handleAboutUsChange("backgroundColor", value === "transparent" ? "transparent" : value)
            }
            description="Set background color for entire About Us section"
            allowTransparent={true}
          />
        </div>
      </div>

      {/* Color Customization for About Us Section */}
      <ColorSection
        title="About Us"
        colors={safeAboutUsColors}
        onColorChange={handleColorChange}
        useSectionColors={safeAboutUs.useSectionColors !== false}
        onToggleSectionColors={toggleSectionColors}
      />

      <FontSizeSection
        title="About Us"
        sizes={safeAboutUs.fontSizes || {}}
        onChange={handleFontSizeChange}
        useSectionFontSizes={safeAboutUs.useSectionFontSizes === true}
        onToggleSectionFontSizes={toggleSectionFontSizes}
        fields={[
          { key: "heading", label: "Title Size", placeholder: "e.g., 40px" },
          { key: "subHeading", label: "Subtitle Size", placeholder: "e.g., 18px" },
          { key: "content", label: "Description Size", placeholder: "e.g., 16px" },
          { key: "stats", label: "Stats Number Size", placeholder: "e.g., 48px" },
          { key: "button", label: "Button Text Size", placeholder: "e.g., 14px" },
        ]}
      />
    </div>
  );
}
